import { type Transaction } from "@tiptap/pm/state";

import { mergeInlineEditables } from "@/components/input/utils/mergeInlineEditables";
import { cursorIsActive } from "@/models/CursorModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { afterDelayedFocus } from "@/utils/afterDelayedFocus";
import { keyMatcher } from "@/utils/keyMatcher";

import type { DocumentModel } from "@/models/DocumentModel";
import type { WrapperNodeConstructorProps } from "@corca/data";
import type { CodeBlockAttrs, GraphNodeAttrs, InlineNodeAttrs } from "@corca/tiptap";
import type { Fragment, Node as ProsemirrorNode } from "@tiptap/pm/model";

export function findDeletedExpressionNodes(transaction: Transaction, doc: DocumentModel) {
  const getNodesIds = (fragment: Fragment) => {
    const ids = new Set<string>();
    fragment.descendants((node) => {
      if (nodeIsExpression(node)) {
        ids.add(node.attrs.nodeId);
      }
      if (nodeIsGraph(node)) {
        ids.add(node.attrs.graphId);
      }
    });
    return ids;
  };

  const currentIds = getNodesIds(transaction.doc.content);
  const previousIds = getNodesIds(transaction.before.content);

  if (currentIds.size === 0 && previousIds.size === 0) {
    return [];
  }

  const deletedIds = [...previousIds].filter((id) => !currentIds.has(id));

  if (deletedIds.length === 0) {
    return [];
  }

  return deletedIds.map((id) => doc.nodes.get(id)).filter(Boolean);
}

export function findDeletedCodeCells(transaction: Transaction) {
  const getNodesIds = (fragment: Fragment) => {
    const ids = new Set<string>();
    fragment.descendants((node) => {
      if (nodeIsCodeBlock(node)) {
        ids.add(node.attrs.cellId);
      }
    });
    return ids;
  };

  const currentIds = getNodesIds(transaction.doc.content);
  const previousIds = getNodesIds(transaction.before.content);

  if (currentIds.size === 0 && previousIds.size === 0) {
    return [];
  }

  return [...previousIds].filter((id) => !currentIds.has(id));
}

export function insertInlineExpressionAndFocus(
  doc: DocumentModel,
  props?: WrapperNodeConstructorProps,
) {
  const { tiptap } = doc.editor;
  if (!tiptap) {
    return;
  }

  const newInlineNode = doc.create(InlineNodeModel);

  tiptap.commands.insertInlineExpression(newInlineNode, undefined, props);

  doc.cursor.moveTo(newInlineNode, 0);
}

export function moveToMath(
  doc: DocumentModel,
  node: ProsemirrorNode | null,
  options: { placeCursorToEnd: boolean } = { placeCursorToEnd: false },
) {
  const wrapperNodeId = node?.attrs.nodeId as string;
  const wrapperNode = doc.textNode.wrapperNodes.find((n) => n.id === wrapperNodeId);
  const inlineNode = wrapperNode?.inlineNode;
  const index = options.placeCursorToEnd ? inlineNode?.nodes.length : 0;
  doc.cursor.moveTo(inlineNode, index);
}

export function postProcessNodeRemoval(doc: DocumentModel, value?: string) {
  mergeInlineEditables(doc);
  removeEmptyInlineExpressionIfNeeded(doc, value);
}

export function removeEmptyInlineExpressionIfNeeded(doc: DocumentModel, value?: string) {
  const inlineExpressionPosition = doc.cursor.parentTiptapNode.position;
  if (
    cursorIsActive(doc.cursor) &&
    doc.cursor.parentWrapperNode?.isEmpty &&
    inlineExpressionPosition !== -1 &&
    nodeIsExpression(doc.cursor.parentTiptapNode.node)
  ) {
    doc.editor.tiptap?.commands.deleteRange({
      from: inlineExpressionPosition,
      to: inlineExpressionPosition + 1,
    });
    doc.cursor.reset();
    if (value) {
      doc.editor.tiptap?.chain().insertContent(value).focus(inlineExpressionPosition).run();
    } else {
      doc.editor.tiptap?.chain().focus(inlineExpressionPosition).run();
    }
  }
}

// @see https://github.com/ueberdosis/tiptap/pull/5590
function allowCopyPasteInInput(event: Event) {
  /* eslint-disable */
  const { target } = event as any;
  if (!target) {
    return false;
  }

  const isDragEvent = event.type.startsWith("drag");
  const isDropEvent = event.type === "drop";
  const isCopyPasteEvent = ["copy", "paste", "cut"].includes(event.type);
  const isInput =
    ["INPUT", "BUTTON", "SELECT", "TEXTAREA"].includes(target.tagName) || target.isContentEditable;

  if (isInput && !isDropEvent && !isDragEvent && !isCopyPasteEvent) {
    return true;
  }

  return false;
  /* eslint-enable */
}

export function stopTiptapEvent(event: Event, doc: DocumentModel) {
  const isEditingNode = !!doc.input.editingNode;

  if (isEditingNode) {
    return true;
  }

  if (event instanceof KeyboardEvent) {
    if (keyMatcher("Mod+KeyZ", event) || keyMatcher("Mod+Shift+KeyZ", event)) {
      return false;
    }
  }
  return allowCopyPasteInInput(event);
}

export function createWrapperNode(
  doc: DocumentModel,
  newInlineNode?: InlineNodeModel,
  props?: WrapperNodeConstructorProps,
): string {
  return doc.transact(() => {
    const inlineNode = newInlineNode || doc.create(InlineNodeModel);
    const wrapperNode = doc.create(WrapperNodeModel, inlineNode, undefined, props);
    doc.textNode.appendChild(wrapperNode);
    if (!newInlineNode) {
      afterDelayedFocus(() => doc.cursor.moveTo(inlineNode, 0));
    }
    return wrapperNode.id;
  }, "TiptapExpression:createWrapperNode");
}

export interface PMNodeWithAttrs<T extends ProsemirrorNode["attrs"]> extends ProsemirrorNode {
  attrs: T;
}

export function nodeIsExpression(
  node?: ProsemirrorNode | null,
): node is PMNodeWithAttrs<InlineNodeAttrs> {
  return node?.type.name === "inlineExpression";
}

export function nodeIsGraph(
  node?: ProsemirrorNode | null,
): node is PMNodeWithAttrs<GraphNodeAttrs> {
  return node?.type.name === "graph";
}

export function nodeIsCodeBlock(
  node?: ProsemirrorNode | null,
): node is PMNodeWithAttrs<CodeBlockAttrs> {
  return node?.type.name === "codeBlock";
}
